import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { Rights } from 'src/app/models/enums/rights';
import { Role } from 'src/app/models/role';
import { MemoryService } from 'src/app/services/memory.service';
import { RolesService } from 'src/app/services/roles.service';
import { FormMode } from '../../../controls/common/form-mode';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { Controls } from '../../../controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from '../../../controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from '../../../controls/forms/management-form/management-form';
import { IUser } from '../../../models/interfaces/user.interface';
import { UserFormContext } from '../user-form-context';

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.scss'],
    imports: [FormsModule, FormFocusDirective, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, MatButtonModule, NgClass],
})
export class UserFormComponent extends ManagementFormComponent<IUser> implements OnInit, AfterViewInit, OnChanges {
    public faSave = faSave;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos de l'utilisateur à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('user')
    public dataInput: Required<IUser> | null | undefined;

    @Input()
    public isProfile: boolean;

    /**
     * Contexte du formulaire (mode d'affichage, groupes proposés dans la liste déroulante Groupe parent du formulaire, rôles ...).
     */
    public _context: UserFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<UserFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: UserFormContext) {
        this._context$.next(value);
    }

    /**
     * Liste des rôles proposés dans la liste déroulante rôle du formulaire.
     */
    public roles: Role[];

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('userUpdated')
    public dataOutput: EventEmitter<IUser> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    public userRolesModifyRight: boolean = false;

    public formValuesChanges: boolean = false;

    @ViewChild('roleselect') roleselect: MatSelect;

    /**
     * Constructeur
     * @param rolesService Instance du service des rôles.
     */
    constructor(
        protected memoryService: MemoryService,
        private rolesService: RolesService
    ) {
        super();

        this.userRolesModifyRight = memoryService.getConnectedUser().hasRight(Rights.ModifyUserRoles);
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de groupe parents
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);
        });

        this.rolesService.ListAttributable().subscribe((roles) => {
            // Tous les rôles sauf SuperAdmin
            this.roles = roles.value.filter((x) => x.id !== 0);
        });

        this.formGroup.valueChanges.subscribe((x) => {
            this.formValuesChanges = true;
        });
    }

    ngAfterViewInit() {
        this.roleselect.openedChange.subscribe((opened) => {
            if (opened) {
                this.roleselect.panel.nativeElement.addEventListener('mouseleave', () => {
                    this.roleselect.close();
                });
            }
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
        this.formGroup.get('email').disable();
        this.formGroup.get('displayName').disable();
        this.formGroup.get('phoneNumber').disable();
        this.formGroup.get('mobileNumber').disable();
        this.formGroup.get('faxNumber').disable();
        this.formGroup.get('culture').disable();
        this.formGroup.get('account').disable();

        if (this.isProfile || this.userRolesModifyRight === false) {
            this.formGroup.get('roles').disable();
        }
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IUser> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            email: new UntypedFormControl(null),
            displayName: new UntypedFormControl(null),
            phoneNumber: new UntypedFormControl(null),
            mobileNumber: new UntypedFormControl(null),
            faxNumber: new UntypedFormControl(null),
            culture: new UntypedFormControl(null),
            account: new UntypedFormControl(null),
            roles: new UntypedFormControl(null),
            service: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
